import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import type { Omittable } from "@salestrak/ui";
import { AGGREGATION_KEY_SEPARATOR } from "@salestrak/widgets";

import { useMemberEntityResolver } from "#app/lib/data";
import type { EntityData } from "#app/lib/entity-visuals";
import {
  useWorkspaceData,
  type WorkspaceWorkflow,
} from "#app/lib/data/workspace-data-provider";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { ARCHIVED_OUT } from "#app/widgets/dashboards/use-dimension-slices";
import { useWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { UNASSIGNED_DIMENSION_KEY } from "#app/widgets/dashboards/use-dimension-counts";
import { useTeamScope } from "../shared/use-team-scope";

/** One workflow line: the workflow, how many projects in scope live in it,
 *  and who is working in it. */
export type WorkflowRosterRow = {
  workflow: WorkspaceWorkflow;
  total: number;
  /** The assignees with at least one project in this workflow, most projects
   *  first. On a team board only the team's members count; elsewhere every
   *  assignee does. A `null` member is an id the directory has not answered
   *  for yet — the stack still reserves the face. */
  members: WorkflowRosterMember[];
};

export type WorkflowRosterMember = {
  userId: string;
  projects: number;
  entity: EntityData | null;
};

/**
 * Fold the two aggregations into rows. `totals` is the per-workflow project
 * count (the plain `workflowId` token, the unassigned bucket dropped);
 * `pairs` the per-(workflow, assignee) count. `workflowIds` is the roster —
 * every id renders, a workflow with nothing in it included, because the card
 * answers "where can this team's work go" as much as "where is it". Rows
 * rank by count, then name, so an empty workflow sits at the foot but is
 * still on the card.
 */
export function buildWorkflowRoster(
  workflowIds: readonly string[],
  workflowMap: ReadonlyMap<string, WorkspaceWorkflow>,
  totals: Record<string, number>,
  pairs: Record<string, number>,
  memberFilter: ReadonlySet<string> | null,
  resolveMember: (userId: string) => EntityData | null,
): WorkflowRosterRow[] {
  const membersByWorkflow = new Map<string, WorkflowRosterMember[]>();
  for (const [key, projects] of Object.entries(pairs)) {
    const separator = key.indexOf(AGGREGATION_KEY_SEPARATOR);
    if (separator < 1) continue;
    const workflowId = key.slice(0, separator);
    const userId = key.slice(separator + AGGREGATION_KEY_SEPARATOR.length);
    if (workflowId === UNASSIGNED_DIMENSION_KEY) continue;
    if (memberFilter && !memberFilter.has(userId)) continue;
    const list = membersByWorkflow.get(workflowId) ?? [];
    list.push({ userId, projects, entity: resolveMember(userId) });
    membersByWorkflow.set(workflowId, list);
  }
  const rows: WorkflowRosterRow[] = [];
  for (const workflowId of workflowIds) {
    const workflow = workflowMap.get(workflowId);
    // An id the workspace map cannot place is a workflow this viewer has no
    // row for; it has no name to print and no chip to draw.
    if (!workflow || workflow.archived) continue;
    const members = (membersByWorkflow.get(workflowId) ?? []).sort(
      (a, b) => b.projects - a.projects || a.userId.localeCompare(b.userId),
    );
    rows.push({ workflow, total: totals[workflowId] ?? 0, members });
  }
  return rows.sort(
    (a, b) =>
      b.total - a.total || (a.workflow.name ?? "").localeCompare(b.workflow.name ?? ""),
  );
}

/**
 * The card's read. On a team board the roster is the team's reach
 * (`Team.workflowIds`) and only the team's own members stack on a row; on
 * every other board it is every live workflow the viewer can see, with every
 * assignee. Both counts fold the widget's own Filters blob over the ambient
 * scope, exactly as the dimension readers do, so the row totals agree with a
 * workload bar beside them.
 */
export function useWorkflowRoster(filters: unknown): Omittable<{
  rows: WorkflowRosterRow[];
  loading: boolean;
  error?: ErrorLike;
}> {
  const { teamId, team, loading: teamPending, error: teamError } = useTeamScope();

  const { base, override } = useWidgetFilters(filters);
  const baseWhere = useMemo(
    () => (Object.keys(base).length > 0 ? { AND: [ARCHIVED_OUT, base] } : ARCHIVED_OUT),
    [base],
  );
  const { where } = useScopedWhere(baseWhere, { override });
  const totals = useIssueGroupCounts({ where, groupBy: "workflowId" });
  const pairs = useIssueGroupCounts({ where, groupBy: "workflowId.assignee" });

  const { workflows, workflowMap } = useWorkspaceData();
  const resolveMember = useMemberEntityResolver();

  const rows = useMemo(() => {
    const roster = teamId
      ? (team?.workflowIds ?? [])
      : workflows.filter((workflow) => !workflow.archived).map((workflow) => workflow.workflowId);
    const memberFilter = teamId
      ? new Set([...(team?.leadIds ?? []), ...(team?.memberIds ?? [])])
      : null;
    return buildWorkflowRoster(
      roster,
      workflowMap,
      totals.counts,
      pairs.counts,
      memberFilter,
      resolveMember,
    );
  }, [teamId, team, workflows, workflowMap, totals.counts, pairs.counts, resolveMember]);

  return {
    rows,
    loading: teamPending || !totals.ready || !pairs.ready,
    error: teamError ?? totals.error ?? pairs.error,
  };
}
